/**
 * Live ticket changes for a view that holds its OWN snapshot of ticket rows —
 * the desk (TicketDeskStore) and Client 360's Tickets tab (ClientTicketsStore).
 * A THIN SELECTOR over the shared realtime bus (`src/realtime/`).
 *
 * Both stores treat their rows as a snapshot and re-read after every write of
 * their own ("every write re-reads"), so a change made by SOMEONE ELSE never
 * reached them: the `tickets` ServiceStore merged the event and nothing on
 * screen read it (#3820). The answer here is the same one their own writes
 * use — re-read — because the desk's scopes, search and paging are decided by
 * the server, and an edited ticket can enter or leave a scope.
 *
 * WHO HEARS WHAT IS THE SERVER'S CALL: the API sends a ticket row only to the
 * accounts its read admits (packages/api/src/channels.ts
 * `ticketReaderChannels`), so nothing arrives here that this person could not
 * already read on the desk.
 */
import type RootStore from "stores/RootStore";
import { subscribeRealtime, ticketCrudChannel } from "realtime";

/** How long a burst of ticket events is gathered into one re-read. */
export const TICKETS_LIVE_DEBOUNCE_MS = 250;

/**
 * Call `reload` once per burst of ticket events that `isRelevant` accepts,
 * with the ids those events touched. Returns an idempotent disposer, which
 * also drops a re-read still waiting to run.
 *
 * shortcut: a whole-list re-read per burst. Exact (the server applies the
 * scope) and cheap at the rate tickets move; patch rows in place if a desk
 * ever holds more than its one page.
 */
export function watchTicketChanges(
  rootStore: RootStore,
  isRelevant: (ticket: Record<string, unknown>) => boolean,
  reload: (touched: ReadonlySet<number>) => void,
  debounceMs: number = TICKETS_LIVE_DEBOUNCE_MS
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let touched = new Set<number>();
  const unsubscribe = subscribeRealtime(rootStore, ticketCrudChannel, (event) => {
    if (!isRelevant(event.record)) return;
    const id = Number(event.record.id);
    if (Number.isFinite(id)) touched.add(id);
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      const ids = touched;
      touched = new Set();
      reload(ids);
    }, debounceMs);
  });
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    if (timer) clearTimeout(timer);
    timer = null;
    touched = new Set();
    unsubscribe();
  };
}
